/**
 * Copyright 2026 Bluecircle Technologies Pvt Limited. All rights reserved.
 *
 * This software and its source code are the proprietary and confidential
 * intellectual property of Bluecircle Technologies Pvt Limited. Unauthorized
 * copying, reproduction, distribution, modification, public display, or use
 * of this software, in whole or in part, is strictly prohibited without the
 * prior written permission of Bluecircle Technologies Pvt Limited.
 */

/**
 * The one selected workspace (hermes-agent-ui openspec one-selected-workspace): a
 * workspace menu or sidebar always shows exactly one workspace — the one in the URL,
 * else the site's last used one, else its Home workspace, else its first.
 */

/** Where Kue, Apps and KnetDB record each site's last used workspace (flow.knetai.com). */
export const LAST_WORKSPACE_KEY = "last_selected_workspace_by_org";

/** Ids may arrive as numbers or digit strings; names may be missing. */
export interface WorkspaceRef { id: number | string; name?: string }

export function selectWorkspace<W extends WorkspaceRef>(
  urlWs: number | undefined | null,
  lastUsedWs: number | undefined | null,
  workspaces: readonly W[],
): W | undefined {
  const byId = (id: number | undefined | null) => (id == null ? undefined : workspaces.find(w => Number(w.id) === id));
  return byId(urlWs) ?? byId(lastUsedWs) ??
    workspaces.find(w => (w.name ?? "").trim().toLowerCase() === "home") ?? workspaces[0];
}

/** The site's recorded workspace in the stored map, or undefined. */
export function lastUsedWorkspace(stored: string | null, orgId: number): number | undefined {
  try {
    const map: unknown = JSON.parse(stored ?? "{}");
    const value = map && typeof map === "object" ? (map as Record<string, unknown>)[String(orgId)] : undefined;
    const id = Number(value);
    return Number.isInteger(id) && id > 0 ? id : undefined;
  }
  catch {
    return undefined;
  }
}

/** The stored map with `orgId` → `wsId` recorded, other sites kept. */
export function recordWorkspace(stored: string | null, orgId: number, wsId: number): string {
  let map: Record<string, unknown> = {};
  try {
    const value: unknown = JSON.parse(stored ?? "{}");
    if (value && typeof value === "object" && !Array.isArray(value)) { map = value as Record<string, unknown>; }
  }
  catch {
    map = {};
  }
  return JSON.stringify({ ...map, [String(orgId)]: String(wsId) });
}
